/**
 * db-context.tsx — React bindings for the wa-sqlite database layer.
 *
 * Exports:
 *   - DBProvider   — wraps app, initializes DB, renders via Render prop
 *   - useDB        — returns { db } context (typed as CtxAsync)
 *   - useQuery     — reactive SQL query hook (re-runs on table mutations)
 *   - CtxAsync     — type alias for the DB context
 *   - first        — utility: first element of an array or undefined
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  type ReactNode,
} from 'react'
import {
  openDatabase,
  terminateWorker,
  clearDatabase,
  type Database,
} from './database'
import { initialPull } from './sync-client'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The context object wrapping a Database instance. */
export interface CtxAsync {
  db: Database
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/**
 * We store databases by name so multiple DBProviders (or re-renders with
 * different dbnames) don't clobber each other.
 */
const DBContext = createContext<Map<string, Database>>(new Map())

// ---------------------------------------------------------------------------
// DBProvider
// ---------------------------------------------------------------------------

interface DBProviderProps {
  dbname: string
  schema: {
    name: string
    content: string
  }
  Render: () => ReactNode
}

/**
 * Check if the local database has any data rows. If all data tables are
 * empty, the database is considered fresh (just-created by schema DDL).
 */
async function isDatabaseEmpty(db: Database): Promise<boolean> {
  // Check a few representative tables — if any have rows, DB has data.
  for (const table of ['programs', 'exercises', 'completed_sets']) {
    const rows = await db.execO<{ cnt: number }>(
      `SELECT count(*) as cnt FROM "${table}"`
    )
    if (rows[0]?.cnt > 0) return false
  }
  return true
}

// Corruption recovery is handled by terminateWorker() + clearDatabase()
// from database.ts — no IDB deletion needed since data is now in OPFS.

/**
 * Initializes a SQLite database (via wa-sqlite) and provides it to the
 * component tree.
 *
 * On first open for a profile, if the local database is empty and the
 * browser is online, performs an initial pull from the server to hydrate
 * the database with existing data.
 *
 * If the database is corrupt (e.g., from a previous crash), clears the
 * OPFS backing store and retries once.
 *
 *   <DBProvider dbname={profileId} schema={{ name, content }} Render={() => <App />} />
 */
export function DBProvider({ dbname, schema, Render }: DBProviderProps) {
  const parentMap = useContext(DBContext)
  const [dbMap, setDbMap] = useState<Map<string, Database>>(parentMap)
  const [readyDbname, setReadyDbname] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const dbRef = useRef<Database | null>(null)

  useEffect(() => {
    let cancelled = false

    async function init(retried = false) {
      let db: Database
      try {
        db = await openDatabase(dbname, schema.content)
      } catch (err) {
        // If the database is corrupt and we haven't retried yet,
        // terminate the Worker, clear OPFS data, and try again.
        if (!retried) {
          console.warn(
            `[DBProvider] database "${dbname}" corrupt, clearing OPFS and retrying:`,
            err
          )
          try {
            terminateWorker()
            await clearDatabase(dbname)
          } catch (clearErr) {
            console.warn(
              '[DBProvider] failed to clear corrupt database:',
              clearErr
            )
          }
          return init(true)
        }
        // If retry also fails, surface the error
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
        return
      }

      if (cancelled) {
        db.close()
        return
      }

      // If the local DB is empty and we're online, pull data from the server.
      // This handles the case where a user logs in on a new device (or after
      // clearing browser data) and the server already has their profile data.
      try {
        const empty = await isDatabaseEmpty(db)
        if (empty && navigator.onLine) {
          console.log(
            `[DBProvider] fresh database "${dbname}", pulling from server...`
          )
          const count = await initialPull(db, dbname)
          if (count > 0) {
            console.log(`[DBProvider] initial pull: ${count} changes applied`)
          } else if (count === 0) {
            console.log(
              `[DBProvider] initial pull: server has no data for this profile`
            )
          }
          // count === -1 means pull was skipped (auth failure, network error, etc.)
          // That's fine — the app will work with an empty DB and sync later.
        }
      } catch (err) {
        // Non-fatal: the app can still work with an empty local DB.
        // The regular sync flow will pick up later.
        console.warn('[DBProvider] initial pull failed (non-fatal):', err)
      }

      if (cancelled) {
        db.close()
        return
      }

      dbRef.current = db
      const next = new Map(parentMap)
      next.set(dbname, db)
      setDbMap(next)
      setReadyDbname(dbname)
    }

    init()

    return () => {
      cancelled = true
      if (dbRef.current) {
        dbRef.current.close()
        dbRef.current = null
      }
    }
  }, [dbname, schema.content])

  // If opening failed, show a minimal error message
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen p-6">
        <div className="max-w-md space-y-4 text-center">
          <p className="text-lg font-semibold text-rose-400">
            Failed to open database
          </p>
          <p className="text-sm text-stone-400 font-mono break-all">
            {error.message}
          </p>
          <button
            className="px-4 py-2 rounded bg-stone-700 text-stone-200 hover:bg-stone-600"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    )
  }

  // Don't render children until the database for the current dbname is ready.
  // This prevents a race where dbname changes but the effect hasn't opened
  // the new database yet, causing useDB(newDbname) to throw.
  if (readyDbname !== dbname) return null

  return (
    <DBContext.Provider value={dbMap}>
      <Render />
    </DBContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// useDB
// ---------------------------------------------------------------------------

/**
 * Returns the CtxAsync for a given database name.
 */
export function useDB(dbname: string): CtxAsync {
  const map = useContext(DBContext)
  const db = map.get(dbname)
  if (!db) {
    throw new Error(
      `useDB("${dbname}"): database not found. Is <DBProvider> mounted?`
    )
  }
  return { db }
}

// ---------------------------------------------------------------------------
// useQuery
// ---------------------------------------------------------------------------

/**
 * Extract table names referenced in a SQL string.
 * Covers FROM, JOIN, INSERT INTO, UPDATE, DELETE FROM patterns.
 */
function extractTableNames(sql: string): string[] {
  const tables = new Set<string>()
  const patterns = [
    /\bFROM\s+"?(\w+)"?/gi,
    /\bJOIN\s+"?(\w+)"?/gi,
    /\bINTO\s+"?(\w+)"?/gi,
    /\bUPDATE\s+"?(\w+)"?/gi,
  ]
  for (const pattern of patterns) {
    let match
    while ((match = pattern.exec(sql)) !== null) {
      tables.add(match[1].toLowerCase())
    }
  }
  return Array.from(tables)
}

interface UseQueryResult<T> {
  data: T[]
  loading: boolean
  error?: Error
}

/**
 * Reactive SQL query hook. Runs the query and re-runs it whenever a relevant
 * table is mutated (detected via update_hook).
 *
 * @param ctx      The CtxAsync from useDB / useProfileDB
 * @param sql      SQL query string
 * @param params   Optional bind parameters
 * @param deps     Optional explicit table dependencies (overrides auto-detection)
 */
export function useQuery<T = Record<string, any>>(
  ctx: CtxAsync,
  sql: string,
  params?: any[],
  deps?: string[]
): UseQueryResult<T> {
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>()
  const versionRef = useRef(0)

  // Memoize the table deps to avoid re-subscribing on every render
  const tableDeps = deps ?? extractTableNames(sql)

  const runQuery = useCallback(async () => {
    try {
      const result = await ctx.db.execO<T>(sql, params)
      setData(result)
      setError(undefined)
    } catch (err) {
      console.error('useQuery error:', err)
      setError(err instanceof Error ? err : new Error(String(err)))
      setData([])
    } finally {
      setLoading(false)
    }
  }, [ctx.db, sql, JSON.stringify(params)])

  // Run query on mount and when sql/params change
  useEffect(() => {
    runQuery()
  }, [runQuery])

  // Subscribe to mutations and re-run when relevant tables change
  useEffect(() => {
    const unsub = ctx.db.onUpdate(() => {
      // The update_hook doesn't tell us which table changed in our
      // simplified listener, so we re-run on any mutation.
      versionRef.current++
      runQuery()
    })

    return unsub
  }, [ctx.db, runQuery])

  return { data, loading, error }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/** Return the first element of an array, or undefined. */
export function first<T>(arr: T[] | undefined | null): T | undefined {
  if (!arr || arr.length === 0) return undefined
  return arr[0]
}
